import type { LotI, NFTI } from '@tacans/shared-marketplace-layer';
import { useMemo } from 'react';
import { useCurrentBids } from 'src/features/common/collectible/hooks/collectible.hooks';
import { useMyWalletsById } from 'src/features/common/wallet/hooks/use-my-wallets-by-id';
import { getAuctionStatus, getLotType } from 'src/utils/lot.utils';

interface ArgsType {
    nft: NFTI;
    lot: LotI;
}

interface ReturnType {
    approve: boolean;
    sell: boolean;
    pause: boolean;
    buyNow: boolean;
    placeBid: boolean;
    changePrice: boolean;
    viewBids: boolean;
}

export const useLotAvailableActions = ({ nft, lot }: ArgsType): ReturnType => {
    const { isAuction, isFixedPrice } = getLotType(lot?.type);
    const currentBids = useCurrentBids();
    const currentNanoBid = currentBids[lot?.id];
    const myWallets = useMyWalletsById();
    const isLotSelling = !!(lot?.isPublished && lot?.isActive);

    const isOwner = !!myWallets[lot?.owner];

    const { isAuctionStarted, isAuctionInProgress, isAuctionNotEnded } = getAuctionStatus(
        lot?.auctionStartAt,
        lot?.auctionEndAt
    );

    return useMemo<ReturnType>(
        () => ({
            sell:
                !!nft?.isPublished &&
                isOwner &&
                !lot?.isDeleted &&
                !lot?.isPublished &&
                lot &&
                !currentNanoBid &&
                (isAuction ? isAuctionNotEnded : true),
            changePrice: isOwner && !!lot?.isPublished && !currentNanoBid && (isAuction ? isAuctionNotEnded : true),
            pause: isLotSelling && !currentNanoBid && isOwner,
            buyNow: !isOwner && isFixedPrice && isLotSelling,
            placeBid: isAuction && !isOwner && (!isAuctionStarted || isAuctionInProgress) && isLotSelling,
            approve:
                !!currentNanoBid && !!(isOwner && lot?.isPublished && isAuction && !isAuctionNotEnded && isLotSelling),
            viewBids: isAuctionStarted && !!lot.isActive,
        }),
        [
            isAuctionStarted,
            currentNanoBid,
            isOwner,
            isAuction,
            isAuctionInProgress,
            isAuctionNotEnded,
            isFixedPrice,
            isLotSelling,
            lot,
            nft?.isPublished,
        ]
    );
};
